/**
 * "Set static camera here": stand at the camera, point the phone the way the lens points, tap Set.
 * Captures the phone's position (± accuracy) and its compass heading (degrees true); ±1°/±5° nudges
 * and a typed heading are the fallback — and the only way on devices without a compass.
 */
import { useEffect, useRef, useState } from 'react';
import type { LatLon } from '@apos/shared';
import { Button, Icon, TextInput } from '../../components/ui';
import { compass, useCompassState } from '../../lib/compass';
import { aimText, nudgeHeading, parseHeading } from './setupModel';
import { getPhoneFix } from './phoneFix';

export interface PhoneAimProps {
  position: LatLon | null;
  headingDeg: number | null;
  onPosition(p: LatLon): void;
  onHeading(h: number | null): void;
  /** Editing a camera that is already placed: "Move here" / "Re-aim from my phone" separately. */
  existing?: boolean;
  disabled?: boolean;
}

export function PhoneAim({ position, headingDeg, onPosition, onHeading, existing, disabled }: PhoneAimProps) {
  const [aiming, setAiming] = useState(false);
  /** Only readings from after the tap count (never freeze a heading left over from earlier). */
  const [aimSince, setAimSince] = useState(0);
  const [locating, setLocating] = useState(false);
  const [accuracyM, setAccuracyM] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const cs = useCompassState();
  const started = useRef(false);

  const stopCompass = () => {
    if (!started.current) return;
    started.current = false;
    compass.stop();
  };
  useEffect(() => stopCompass, []); // eslint-disable-line react-hooks/exhaustive-deps

  const noCompass = cs.status === 'unsupported' || cs.status === 'denied';
  // A device without a compass: fall back to the typed heading.
  useEffect(() => {
    if (aiming && noCompass) {
      setAiming(false);
      stopCompass();
    }
  }, [aiming, noCompass]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Must run inside the tap (iOS Safari asks for motion permission only from a gesture). */
  const startAim = () => {
    if (!started.current) {
      started.current = true;
      void compass.start();
    }
    setAimSince(Date.now() - 250);
    setAiming(true);
  };

  const locate = async () => {
    setErr(null);
    setLocating(true);
    try {
      const fix = await getPhoneFix();
      compass.setPosition(fix); // magnetic → true at this spot
      setAccuracyM(fix.accuracyM);
      onPosition({ lat: fix.lat, lon: fix.lon });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLocating(false);
    }
  };

  const useHere = () => {
    startAim();
    void locate();
  };

  const liveHeading = aiming && cs.t !== null && cs.t >= aimSince ? cs.headingDeg : null;

  const set = () => {
    if (liveHeading !== null) onHeading(nudgeHeading(liveHeading, 0));
    setAiming(false);
    stopCompass();
  };

  return (
    <div className="aim" data-testid="phone-aim">
      {position ? (
        <div className="aim__pos">
          <Icon name="pin" size={16} />
          <span className="num aim__coords" data-testid="aim-position">
            {position.lat.toFixed(5)}, {position.lon.toFixed(5)}
            {accuracyM != null ? <span className="aim__acc"> ±{Math.round(accuracyM)} m</span> : null}
          </span>
          <Button size="sm" variant="secondary" icon="location" onClick={() => void locate()} loading={locating} disabled={disabled}>
            Move here
          </Button>
        </div>
      ) : (
        <Button variant="secondary" size="lg" block icon="location" onClick={useHere} loading={locating} disabled={disabled}>
          Use my phone here
        </Button>
      )}

      {aiming ? (
        <div className="aim__live" role="group" aria-label="Aim with the phone">
          <div className="aim__prompt">Point your phone the way the lens points</div>
          <div className="aim__reading num" aria-live="polite" data-testid="aim-reading">
            {liveHeading === null ? 'Reading compass…' : aimText(liveHeading, cs.accuracyDeg)}
          </div>
          <div className="aim__actions">
            <Button variant="ghost" onClick={() => (setAiming(false), stopCompass())}>
              Cancel
            </Button>
            <Button variant="primary" size="xl" block onClick={set} disabled={liveHeading === null}>
              Set
            </Button>
          </div>
        </div>
      ) : null}

      <div className="aim__heading">
        <label className="field__label" htmlFor="aim-hdg">
          Facing (°)
        </label>
        <div className="aim__heading-row">
          <HeadingInput value={headingDeg} onCommit={onHeading} disabled={disabled} />
          {[-5, -1, 1, 5].map((d) => (
            <Button key={d} variant="secondary" className="aim__nudge num" disabled={disabled || headingDeg === null} onClick={() => onHeading(nudgeHeading(headingDeg, d))} aria-label={`${d > 0 ? 'Plus' : 'Minus'} ${Math.abs(d)} degree${Math.abs(d) === 1 ? '' : 's'}`}>
              {d > 0 ? `+${d}` : `−${-d}`}
            </Button>
          ))}
        </div>
        {!aiming && (position || existing) && !noCompass ? (
          <Button size="sm" variant="secondary" icon="compass" onClick={startAim} disabled={disabled}>
            Re-aim from my phone
          </Button>
        ) : null}
      </div>

      {noCompass ? (
        <p className="aim__note" role="status">
          {cs.status === 'denied' ? 'Compass access was declined — type the heading.' : 'No compass on this device — type the heading.'}
        </p>
      ) : null}
      {err ? (
        <div className="form-error" role="alert">
          {err}
        </div>
      ) : null}
    </div>
  );
}

function HeadingInput({ value, onCommit, disabled }: { value: number | null; onCommit(h: number | null): void; disabled?: boolean }) {
  const [text, setText] = useState(value === null ? '' : String(value));
  // follow outside changes (compass Set, nudges) without fighting what is being typed
  useEffect(() => {
    setText((t) => (parseHeading(t) === value ? t : value === null ? '' : String(value)));
  }, [value]);
  return (
    <TextInput
      id="aim-hdg"
      mono
      inputMode="numeric"
      className="aim__hdg-input"
      value={text}
      disabled={disabled}
      onChange={(e) => {
        const t = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
        setText(t);
        const h = parseHeading(t);
        if (h !== value) onCommit(h);
      }}
      placeholder="—"
    />
  );
}
